import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { NAV } from '../data/site';
import { PHOTO } from '../data/media';
import { ArrowRight } from './Icons';
import { useMediaQuery } from '../hooks/useReveal';
import './NextUp.css';

/**
 * Continuous reading flow.
 *
 * Every page ends with an "up next" panel. Once it is on screen and you keep
 * scrolling down, a ring fills; when it completes, the next page loads. Stop
 * scrolling and it drains back — so you can rest at the bottom of a page
 * without being yanked somewhere.
 *
 * Deliberate design decisions:
 *  - It never fires from a single flick. INTENT_TO_ADVANCE is measured in
 *    accumulated pixels of over-scroll, so it takes a sustained push.
 *  - It is disabled entirely under prefers-reduced-motion, and on the 404,
 *    where an unexpected navigation would be hostile.
 *  - The panel is a real link, so keyboard and screen-reader users get the
 *    same destination without any scroll mechanics.
 */

const FLOW = NAV.map((n) => n.to);

const ART = {
  '/': PHOTO.plate7,
  '/about': PHOTO.plate5,
  '/whats-on': PHOTO.signatures,
  '/menus': PHOTO.margherita,
  '/bookings': PHOTO.plate1,
  '/contact': PHOTO.spritz,
};

const BLURB = {
  '/': 'Back to the beginning.',
  '/about': 'Why we opened, and what we hold to.',
  '/whats-on': 'Four fixtures the neighbourhood plans around.',
  '/menus': 'Food, drinks and golden hour.',
  '/bookings': 'Tell us when, and how many.',
  '/contact': 'However big or small the request.',
};

/** Pixels of sustained over-scroll needed before we advance. */
const INTENT_TO_ADVANCE = 420;
/** How fast intent drains when you stop pushing (px per frame-ish). */
const DECAY = 14;
/** How close to the bottom counts as "at the end". */
const BOTTOM_SLACK = 4;

export default function NextUp() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const reduced = useMediaQuery('(prefers-reduced-motion: reduce)');

  const idx = FLOW.indexOf(pathname);
  const next = idx === -1 ? null : FLOW[(idx + 1) % FLOW.length];

  const [progress, setProgress] = useState(0);
  const intent = useRef(0);
  const firing = useRef(false);
  const decayTimer = useRef(0);

  const atBottom = useCallback(() => {
    const doc = document.documentElement;
    return window.scrollY + window.innerHeight >= doc.scrollHeight - BOTTOM_SLACK;
  }, []);

  useEffect(() => {
    // reset whenever the route changes
    intent.current = 0;
    firing.current = false;
    setProgress(0);
  }, [pathname]);

  useEffect(() => {
    if (!next || reduced) return undefined;

    const bump = (delta) => {
      if (firing.current || delta <= 0 || !atBottom()) return;
      intent.current = Math.min(INTENT_TO_ADVANCE, intent.current + delta);
      setProgress(intent.current / INTENT_TO_ADVANCE);

      if (intent.current >= INTENT_TO_ADVANCE) {
        firing.current = true;
        setProgress(1);
        navigate(next);
      }
    };

    const onWheel = (e) => bump(e.deltaY);

    let lastTouch = null;
    const onTouchStart = (e) => {
      lastTouch = e.touches[0]?.clientY ?? null;
    };
    const onTouchMove = (e) => {
      const y = e.touches[0]?.clientY;
      if (y == null || lastTouch == null) return;
      bump(lastTouch - y); // finger moving up = scrolling down
      lastTouch = y;
    };

    // Drain intent while the user is not pushing.
    const tick = () => {
      if (!firing.current && intent.current > 0) {
        intent.current = Math.max(0, intent.current - DECAY);
        setProgress(intent.current / INTENT_TO_ADVANCE);
      }
    };
    decayTimer.current = window.setInterval(tick, 90);

    window.addEventListener('wheel', onWheel, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });

    return () => {
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.clearInterval(decayTimer.current);
    };
  }, [next, reduced, navigate, atBottom]);

  if (!next) return null;

  const label = NAV.find((n) => n.to === next)?.label ?? 'Next';
  const pct = Math.round(progress * 100);
  const circumference = 2 * Math.PI * 22;

  return (
    <section className={`nextup${progress > 0.02 ? ' is-arming' : ''}`} aria-label="Continue reading">
      <Link to={next} className="nextup__link">
        <div className="nextup__media" aria-hidden="true">
          <img src={ART[next]} alt="" loading="lazy" />
        </div>

        <div className="page nextup__inner on-dark">
          <p className="eyebrow">Up next</p>

          <div className="nextup__row">
            <div>
              <h2 className="display brand d2 nextup__title">{label}</h2>
              <p className="nextup__blurb">{BLURB[next]}</p>
            </div>

            <span className="nextup__ring" aria-hidden="true">
              <svg className="nextup__svg" viewBox="0 0 48 48" width="56" height="56">
                <circle className="nextup__track" cx="24" cy="24" r="22" />
                <circle
                  className="nextup__fill"
                  cx="24"
                  cy="24"
                  r="22"
                  style={{
                    strokeDasharray: circumference,
                    strokeDashoffset: circumference * (1 - progress),
                  }}
                />
              </svg>
              <ArrowRight size={16} />
            </span>
          </div>

          <p className="nextup__hint">
            {reduced ? (
              <>Tap to continue.</>
            ) : (
              <>
                Keep scrolling to continue
                {pct > 2 ? <span className="nextup__pct tabular"> · {pct}%</span> : null}
              </>
            )}
          </p>
        </div>
      </Link>
    </section>
  );
}
